import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ClipboardCheck, CalendarClock, MessageSquareReply, CalendarDays } from "lucide-react";
import { Btn, Card, PageTitle, StatusChip, DemoBadge, Empty } from "../components/kit";
import { Loading } from "../components/common";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtDateTime, fmtLocal } from "../lib/format";

function Section({ icon: Icon, title, count, testid, tint, children }) {
  return (
    <Card className="fade-up" data-testid={testid}>
      <div className="mb-4 flex items-center gap-3">
        <span className={`rounded-xl p-2.5 ${tint}`}><Icon className="h-5 w-5" /></span>
        <h2 className="text-lg font-bold">{title}</h2>
        <span data-testid={`${testid}-count`} className="ml-auto rounded-full bg-muted px-3 py-1 text-sm font-bold">{count}</span>
      </div>
      {children}
    </Card>
  );
}

const Row = ({ to, children, testid }) => (
  <li><Link to={to} data-testid={testid} className="flex min-h-[48px] flex-wrap items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm hover:bg-muted">{children}</Link></li>
);

export default function Home() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/home").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  const none = (k) => <p className="text-sm text-muted-foreground">{t(`home.none_${k}`)}</p>;
  return (
    <div>
      <PageTitle title={t("home.hello", { name: me.user.first_name || me.user.display_name })} sub={t("home.sub")} />
      {d.dog_count === 0 && (
        <div className="mb-6"><Empty testid="home-empty" title={t("home.empty")} action={<Link to="/dogs/new"><Btn testid="home-add-first-dog-btn">{t("home.add_first_dog")}</Btn></Link>} /></div>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        <Section icon={ClipboardCheck} title={t("home.tasks")} count={d.review_tasks.length} testid="home-review-tasks" tint="bg-[hsl(42_90%_92%)]">
          {d.review_tasks.length ? <ul className="space-y-2">{d.review_tasks.map((r) => (
            <Row key={r.id} to={`/tasks/${r.task_id}`} testid={`review-task-${r.number}`}>
              <span className="font-semibold">{r.number}</span><span>{r.dog_name}</span><span className="text-muted-foreground">{r.task_number}</span>
              <span className="ml-auto"><StatusChip status={r.outcome} /></span>
            </Row>))}</ul> : none("tasks")}
        </Section>
        <Section icon={MessageSquareReply} title={t("home.replies")} count={d.replies.length} testid="home-replies" tint="bg-[hsl(var(--lavender))]">
          {d.replies.length ? <ul className="space-y-2">{d.replies.map((r) => (
            <Row key={r.id} to={`/tasks/${r.task_id}`} testid={`reply-${r.number}`}>
              <span className="font-semibold">{r.dog_name}</span><span className="text-muted-foreground">{r.owner_name} · {fmtDateTime(r.created_at, me.user.time_zone)}</span>
              <span className="ml-auto"><StatusChip status={r.outcome} /></span>
            </Row>))}</ul> : none("replies")}
        </Section>
        <Section icon={CalendarClock} title={t("home.due")} count={d.due_programmes.length} testid="home-due-programmes" tint="bg-[hsl(var(--peach))]">
          {d.due_programmes.length ? <ul className="space-y-2">{d.due_programmes.map((p) => (
            <Row key={p.id} to={`/programmes/${p.id}`} testid={`due-${p.number}`}>
              <span className="font-semibold">{p.number}</span><span>{p.dog_name}</span>{p.is_demo && <DemoBadge short />}
              <span className="ml-auto text-muted-foreground">{p.review_date < new Date().toISOString().slice(0, 10) ? t("common.overdue_since", { date: fmtDate(p.review_date) }) : t("home.review_on", { date: fmtDate(p.review_date) })}</span>
            </Row>))}</ul> : none("due")}
        </Section>
        <Section icon={CalendarDays} title={t("home.visits")} count={d.upcoming_visits.length} testid="home-upcoming-visits" tint="bg-[hsl(var(--sage))]">
          {d.upcoming_visits.length ? <ul className="space-y-2">{d.upcoming_visits.map((v) => (
            <Row key={v.id} to={`/visits/${v.id}`} testid={`visit-${v.number}`}>
              <span className="font-semibold">{v.number}</span><span>{v.dog_name}</span>{v.is_demo && <DemoBadge short />}
              <span className="text-muted-foreground">{fmtLocal(v.scheduled_local, v.time_zone)}</span>
              <span className="ml-auto"><StatusChip status={v.status} /></span>
            </Row>))}</ul> : none("visits")}
        </Section>
      </div>
    </div>
  );
}
